import { Upload, X, FileText, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFileUpload, type FileValue } from './use-file-upload';

type FileUploadProps = {
  folder: 'documents' | 'evidence' | 'certificates' | 'thumbnails' | 'avatars';
  accept?: string;
  maxSizeMB?: number;
  value?: FileValue | null;
  onChange: (file: FileValue | null) => void;
  disabled?: boolean;
  label?: string;
};

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function FileUpload({
  folder,
  accept,
  maxSizeMB = 50,
  value,
  onChange,
  disabled = false,
  label,
}: FileUploadProps) {
  const {
    state,
    errorMsg,
    progress,
    dragOver,
    setDragOver,
    inputRef,
    handleDrop,
    handleFileSelect,
    handleRemove,
  } = useFileUpload({ folder, accept, maxSizeMB, value, onChange, disabled });

  if (state === 'uploaded' && value) {
    return (
      <div>
        {label && <p className="mb-1.5 text-sm font-medium text-foreground">{label}</p>}
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-3">
          <FileText className="h-8 w-8 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">{value.name}</p>
            <p className="text-xs text-muted-foreground">{formatFileSize(value.size)}</p>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={handleRemove}
              className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              aria-label="Remover archivo"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {label && <p className="mb-1.5 text-sm font-medium text-foreground">{label}</p>}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled && state !== 'uploading') setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => {
          if (!disabled && state !== 'uploading') inputRef.current?.click();
        }}
        className={cn(
          'flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors',
          dragOver
            ? 'border-primary bg-primary/5'
            : 'border-input hover:border-primary/50 hover:bg-muted/30',
          disabled && 'cursor-not-allowed opacity-50',
          state === 'uploading' && 'cursor-wait',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled || state === 'uploading'}
        />

        {state === 'uploading' ? (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Subiendo archivo...</p>
            <div className="h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </>
        ) : (
          <>
            <Upload className="h-8 w-8 text-muted-foreground" />
            <div className="text-center">
              <p className="text-sm text-foreground">
                Arrastra un archivo o{' '}
                <span className="font-medium text-primary">haz clic para seleccionar</span>
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {accept ? `Formatos: ${accept}` : 'Cualquier formato'} — Máx {maxSizeMB}MB
              </p>
            </div>
          </>
        )}
      </div>

      {state === 'error' && errorMsg && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}
    </div>
  );
}
